import {Flex} from '@mantine/core';
import React from 'react';

import WorkspaceItemInaccessibleGuard from 'auth/WorkspaceItemInaccessibleGuard';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {LockedContext} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/LockedContext';
import {isFlowchartType} from 'processes/processes-platform/FeatureOptions';
import {
  MinimalProcessTaskSchemaEditorQuery,
  MinimalProcessTaskSchemaEditorQueryVariables,
} from 'processes/settings/__generated/MinimalProcessTaskSchemaEditorQuery';
import {
  MINIMAL_PROCESS_TASK_SCHEMA_EDITOR_QUERY,
  ProcessTaskSchemaEditorContent,
  ProcessTaskSchemaEditorContentSections,
  ProcessTaskSchemaEditorTabbedContainer,
} from 'processes/settings/ProcessTaskSchemaEditor';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {ProcessFlowchartMetadataEditorProps} from '../ProcessFlowchartConfigEditorLeftSidepanel';
import {useAllSchemasById} from '../useAllSchemasById';

function FlowchartSchemaConfigurationTab(props: ProcessFlowchartMetadataEditorProps): JSX.Element {
  const {
    processTaskSchemas: allTaskSchemas,
    storableSchemas,
    tagSchemas,
    resultSchemas,
    statusLifecycles,
    haveSchemasLoaded,
  } = useAllSchemasById(props.flowchartTaskSchema);
  const locked = LockedContext.useContext();

  return !haveSchemasLoaded ? (
    <CenteredHorizontalThrobber />
  ) : (
    <ProcessTaskSchemaEditorContent
      noSectionSpacing={true}
      processTaskSchema={{...props.flowchartTaskSchema, flowchartConfig: null, relevantForms: []}}
      storableSchemas={storableSchemas}
      tagSchemas={tagSchemas}
      taskSchemas={
        // We do not need flowchart type task schemas here because they are not needed for basic task configuration
        allTaskSchemas
          ?.filter((schema) => !isFlowchartType(schema.executionType))
          .map((schema) => ({...schema, flowchartConfig: null})) ?? []
      }
      statusLifecycles={statusLifecycles}
      resultSchemas={resultSchemas}
      isAnyPartOfFlowchartConfigInaccessible={null}
      sectionsToHide={[
        ProcessTaskSchemaEditorContentSections.TASK_FIELDS,
        ProcessTaskSchemaEditorContentSections.OUTPUT,
        ProcessTaskSchemaEditorContentSections.FLOWCHART,
      ]}
      locked={locked}
    />
  );
}

export function FlowchartSettingsSidepanelContent(props: ProcessFlowchartMetadataEditorProps): JSX.Element {
  return (
    <Flex pt="md" px="md">
      {/* The ProcessTaskSchemaEditor expects to also see forms and request templates. Query them here */}
      <BenchlingQuery<MinimalProcessTaskSchemaEditorQuery, MinimalProcessTaskSchemaEditorQueryVariables>
        query={MINIMAL_PROCESS_TASK_SCHEMA_EDITOR_QUERY}
        renderLoading={() => <CenteredHorizontalThrobber />}
        variables={{schemaId: props.flowchartTaskSchema.id}}
        fetchPolicy="cache-and-network"
      >
        {(data) =>
          (() => {
            if (isInaccessible(data.processTaskSchema)) {
              return (
                <WorkspaceItemInaccessibleGuard
                  inaccessibleInfo={data.processTaskSchema}
                  modelType="processTaskSchema"
                />
              );
            }
            return (
              <ProcessTaskSchemaEditorTabbedContainer
                subtitle={props.flowchartTaskSchema.name}
                processTaskSchema={data.processTaskSchema}
                configurationTab={
                  <FlowchartSchemaConfigurationTab flowchartTaskSchema={data.processTaskSchema} />
                }
                schemaId={props.flowchartTaskSchema.id}
              />
            );
          })()
        }
      </BenchlingQuery>
    </Flex>
  );
}
